// src/pages/ListingCabs.js
import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Header from "./common/Header";
import Footer from "./common/Footer";
import LoginPopup from "./popup/LoginPopup";
import SEO from "../components/common/SEO";
import { useLoadScript, Autocomplete } from "@react-google-maps/api";
import CabCard from "./CabCard";
import axios from "axios";

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const GOOGLE_MAP_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const libraries = ["places"];

/* -------------------------
   KEEP YOUR SearchForm (same as you provided)
   ------------------------- */
function SearchForm({ initial = {}, onSearch }) {
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAP_KEY,
    libraries,
  });

  const fromAutoRef = useRef(null);
  const toAutoRef = useRef(null);

  const [fromLabel, setFromLabel] = useState(initial.from || "");
  const [toLabel, setToLabel] = useState(initial.to || "");

  const [fromPlace, setFromPlace] = useState(null);
  const [toPlace, setToPlace] = useState(null);

  const normalizeAreaName = (value = "") => {
    const text = String(value || "").trim();
    if (!text) return "";
    const cleaned = text.replace(/\b(?:East|West|North|South|Northeast|Northwest|Southeast|Southwest)\b$/i, "").trim();
    return cleaned || text;
  };

  const placeToLabel = (place) => {
    if (!place) return "";

    const comp = place.address_components || [];
    const findType = (type) => {
      const c = comp.find((x) => x.types && x.types.includes(type));
      return c ? c.long_name : null;
    };

    const rawArea =
      findType("sublocality_level_1") ||
      findType("sublocality") ||
      findType("neighborhood") ||
      findType("administrative_area_level_3") ||
      findType("route");
    const area = normalizeAreaName(rawArea);
    const city =
      findType("locality") ||
      findType("postal_town") ||
      findType("administrative_area_level_2");
    const state = findType("administrative_area_level_1");
    const country = findType("country");

    if (area) return area;
    if (city) return city;
    if (state) return state;
    if (country) return country;
    if (place.formatted_address) return place.formatted_address;
    if (place.name) return place.name;
    return "";
  };

  const extractDetailedPlaceInfo = (place) => {
    if (!place) return {};

    const comp = place.address_components || [];
    const findType = (type) => {
      const c = comp.find((x) => x.types && x.types.includes(type));
      return c ? c.long_name : null;
    };

    // Extract detailed location components
    const areaName = normalizeAreaName(
      findType('sublocality') || findType('sublocality_level_1') || findType('neighborhood')
    );
    const city = findType('locality') || findType('administrative_area_level_2');
    const state = findType('administrative_area_level_1');
    const buildingName = findType('premise') || findType('subpremise') || findType('establishment');
    const companyName = place.name || findType('establishment');

    return {
      areaName,
      city,
      state,
      buildingName,
      companyName,
      formattedAddress: place.formatted_address,
      placeId: place.place_id,
      geometry: place.geometry
    };
  };

  const handlePlaceChanged = (autoRef, setLabel, setPlace) => {
    const inst = autoRef.current;
    if (!inst) return;
    const place = inst.getPlace();
    const label = placeToLabel(place) || (place && place.name) || "";

    // Extract detailed place information
    const detailedInfo = extractDetailedPlaceInfo(place);

    if (label) {
      setLabel(label);
      setPlace({
        label,
        placeId: place.place_id,
        geometry: place.geometry,
        raw: place,
        detailedInfo: detailedInfo
      });
    } else {
      // user may have typed free text — keep typed value (no change)
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const resolvedFrom = String(fromLabel || "").trim() || fromPlace?.label || placeToLabel(fromPlace?.raw);
    const resolvedTo = String(toLabel || "").trim() || toPlace?.label || placeToLabel(toPlace?.raw);

    const newErr = {};
    if (!resolvedFrom) newErr.from = "From is required.";
    if (!resolvedTo) newErr.to = "To is required.";

    if (Object.keys(newErr).length) {
      setErrors(newErr);
      return;
    }

    setErrors({});

    const params = {
      from: resolvedFrom,
      to: resolvedTo,
      // Include detailed location information from Google Places API
      fromDetails: fromPlace?.detailedInfo || {},
      toDetails: toPlace?.detailedInfo || {}
    };

    console.log(params);

    if (typeof onSearch === "function") {
      onSearch(params);
    } else {
      const q = new URLSearchParams(params).toString();
      navigate(`/cabs/search?${q}`);
    }
  };

  return (
    <form className="filter-form" onSubmit={handleSubmit} noValidate>
      {/* From */}
      <div className="form-group" style={{ minWidth: '200px', flex: 1 }}>
        {isLoaded ? (
          <Autocomplete
            onLoad={(inst) => {
              fromAutoRef.current = inst;
              try {
                inst.setFields([
                  "formatted_address",
                  "name",
                  "address_components",
                  "geometry",
                  "place_id",
                ]);
              } catch (e) { }
            }}
            onPlaceChanged={() =>
              handlePlaceChanged(fromAutoRef, setFromLabel, setFromPlace)
            }
            options={{
              types: ["geocode", "establishment"],
              fields: ["formatted_address", "name", "address_components", "geometry", "place_id"]
            }}
          >
            <input
              id="fromInput"
              type="text"
              className={`checkin-out ${fromLabel || fromPlace?.label ? "filter-value-active" : ""} ${errors?.from ? "is-invalid" : ""}`}
              placeholder="Andheri East, Mumbai"
              value={fromLabel}
              onChange={(e) => {
                setFromLabel(e.target.value);
                if (errors?.from)
                  setErrors((prev) => ({ ...prev, from: null }));
              }}
              style={{ width: '100%' }}
            />
          </Autocomplete>
        ) : (
          <input
            id="fromInput"
            type="text"
            className={`checkin-out ${fromLabel || fromPlace?.label ? "filter-value-active" : ""} ${errors?.from ? "is-invalid" : ""}`}
            placeholder="From"
            value={fromLabel}
            onChange={(e) => {
              setFromLabel(e.target.value);
              if (errors?.from)
                setErrors((prev) => ({ ...prev, from: null }));
            }}
            style={{ width: '100%' }}
          />
        )}
        {errors?.from && (
          <div
            className="text-danger"
            style={{ fontSize: ".875em", marginTop: 6 }}
          >
            {errors.from}
          </div>
        )}
      </div>

      {/* To */}
      <div className="form-group form-border-left" style={{ minWidth: '200px', flex: 1 }}>
        {isLoaded ? (
          <Autocomplete
            onLoad={(inst) => {
              toAutoRef.current = inst;
              try {
                inst.setFields([
                  "formatted_address",
                  "name",
                  "address_components",
                  "geometry",
                  "place_id",
                ]);
              } catch (e) { }
            }}
            onPlaceChanged={() =>
              handlePlaceChanged(toAutoRef, setToLabel, setToPlace)
            }
            options={{
              types: ["geocode", "establishment"],
              fields: ["formatted_address", "name", "address_components", "geometry", "place_id"]
            }}
          >
            <input
              id="toInput"
              type="text"
              className={`checkin-out ${toLabel || toPlace?.label ? "filter-value-active" : ""} ${errors?.to ? "is-invalid" : ""}`}
              placeholder="Connaught Place, Delhi"
              value={toLabel}
              onChange={(e) => {
                setToLabel(e.target.value);
                if (errors?.to) setErrors((prev) => ({ ...prev, to: null }));
              }}
              style={{ width: '100%' }}
            />
          </Autocomplete>
        ) : (
          <input
            id="toInput"
            type="text"
            className={`checkin-out ${toLabel || toPlace?.label ? "filter-value-active" : ""} ${errors?.to ? "is-invalid" : ""}`}
            placeholder="To"
            value={toLabel}
            onChange={(e) => {
              setToLabel(e.target.value);
              if (errors?.to) setErrors((prev) => ({ ...prev, to: null }));
            }}
            style={{ width: '100%' }}
          />
        )}
        {errors?.to && (
          <div
            className="text-danger"
            style={{ fontSize: ".875em", marginTop: 6 }}
          >
            {errors.to}
          </div>
        )}
      </div>

      <div className="form-group" style={{ minWidth: '120px', alignSelf: "center", flex: 0 }}>
        <button type="submit" className="submit-btn-filter " style={{ width: '100%', height: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          Search
        </button>
      </div>

      {loadError && (
        <div style={{ color: "red", marginTop: 8, width: '100%' }}>
          Google Maps could not be loaded — autocomplete may not work.
        </div>
      )}

      <style>{`
        .filter-form {
          display: flex !important;
          flex-wrap: wrap !important;
          gap: 15px !important;
          align-items: flex-end !important;
        }
        .filter-form .form-group {
          margin-bottom: 0 !important;
        }
        .filter-form input::placeholder {
          color: #6c757d !important;
          opacity: 1 !important;
        }
        .filter-form select {
          color: #111827;
          border-bottom-color: #32AEE4;
        }
        .filter-form select option {
          color: #111;
        }
        @media (max-width: 767px) {
          .filter-form .form-border-left {
            border-left: none !important;
            padding-left: 0 !important;
          }
          .filter-form .form-group {
            width: 100% !important;
            min-width: 100% !important;
          }
          .filter-form .submit-btn-filter {
            width: 100% !important;
          }
        }
      `}</style>
    </form>
  );
}

/* -------------------------
   CabsListing: header + filter + results
   ------------------------- */
function CabsListing() {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const query = useQuery();
  const navigate = useNavigate();

  const initialFrom = query.get("from") || "";
  const initialTo = query.get("to") || "";

  const [filter, setFilter] = useState({
    from: initialFrom,
    to: initialTo,
  });

  const [listings, setListings] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [totalResults, setTotalResults] = useState(0);

  // called when SearchForm submits
  const handleSearch = async (params, pageNum = 1, isLoadMore = false) => {
    console.log("Search params:", params, "Page:", pageNum);

    if (!isLoadMore) {
      setIsLoading(true);
      setFetchError(null);
      setPage(1);
    } else {
      setLoadingMore(true);
    }

    setFilter({
      from: params.from || "",
      to: params.to || "",
    });

    const serializeParams = (searchParams) => {
      const result = {};
      Object.entries(searchParams).forEach(([key, value]) => {
        if (value === undefined || value === null || value === "") return;
        result[key] = typeof value === 'object' ? JSON.stringify(value) : String(value);
      });
      return result;
    };

    const serializedParams = serializeParams({ ...params, page: pageNum, limit: 20 });
    const q = new URLSearchParams(serializedParams).toString();
    if (!isLoadMore) {
      if (params.from && params.to) {
        navigate(`/cabs/search?${q}`);
      } else {
        navigate(`/cabs/search`);
      }
    }

    try {
      const res = await axios.get(`${API_BASE_URL}/cabs/search`, { params: serializedParams });
      console.log("API response:", res.data);

      const newData = Array.isArray(res.data) ? res.data : (res.data?.data || []);
      const count = res.data?.total || res.data?.count || newData.length;
      const more = res.data?.hasMore || (newData.length === 20);

      if (isLoadMore) {
        setListings((prev) => [...prev, ...newData]);
      } else {
        setListings(newData);
        setTotalResults(count);
      }
      setHasMore(more);
      setPage(pageNum);
    } catch (err) {
      console.error("Listing fetch error:", err);
      setFetchError("Unable to load listings. Try again.");
    } finally {
      setIsLoading(false);
      setLoadingMore(false);
    }
  };

  const handleLoadMore = () => {
    const params = {
      from: filter.from,
      to: filter.to,
    };
    handleSearch(params, page + 1, true);
  };

  useEffect(() => {
    handleSearch({
      from: initialFrom || "",
      to: initialTo || "",
    }, 1, false);
  }, []);

  return (
    <div className="cabs-search-page">
      <SEO title={filter.from && filter.to ? `Cabs from ${filter.from} to ${filter.to} | Mangoloops` : "Affordable Cab Services | Mangoloops"} description="Book reliable and affordable cab services with Mangoloops. Enjoy a comfortable journey with our top-rated cab operators and professional drivers." />
      <LoginPopup />
      <Header />

      <div className="banner-content bg-img-hero text-center mb-5 cabs-search-hero" style={{ backgroundImage: "url('/assets/img/cabs/banner.png')", backgroundSize: 'cover', backgroundPosition: 'center' }}>
        <div className="container-fluid container-package">
          <div className="row justify-content-center py-xl-4">
            <div className="card border-0 border-radius-12 tab-shadow cabs-search-filter-card">
              <div className="card-body padding-14 cabs-search-filter-body">
                <SearchForm
                  initial={{
                    from: filter.from,
                    to: filter.to,
                  }}
                  onSearch={handleSearch}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Results section (visual style similar to your screenshot) */}
      <div className="container cabs-search-results">
        <div className="d-flex justify-content-between align-items-center mb-4 heading-div">
          <h3 className="fw-bold mb-0 text-start heading-div">
            {isLoading ? "Searching..." : "Available Cabs"}
          </h3>
          {!isLoading && (
            <span className="text-muted small">
              Showing {listings.length} results {totalResults > 0 ? `of ${totalResults}` : ""}
            </span>
          )}
        </div>

        {isLoading && page === 1 ? (
          <div>Loading results...</div>
        ) : fetchError ? (
          <div className="text-danger">{fetchError}</div>
        ) : listings.length === 0 ? (
          <div className="text-muted text-center">No results found. Please try a different search.</div>
        ) : (
          <>
            <div style={{ display: "grid", gap: 18 }}>
              {listings.map((l, i) => (
                <CabCard key={l?.id || i} cab={l} />
              ))}
            </div>

            {/* LOAD MORE BUTTON */}
            {hasMore && (
              <div className="text-center mt-5 mb-5">
                <button
                  className="btn btn-primary px-5 py-2 fw-bold rounded-pill shadow-sm"
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  style={{ backgroundColor: '#32AEE4', borderColor: '#32AEE4' }}
                >
                  {loadingMore ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Loading More...
                    </>
                  ) : (
                    "Load More"
                  )}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <Footer />
      <style>{`
        .cabs-search-results {
          padding: 18px 15px;
        }

        @media (max-width: 767px) {
          .cabs-search-page .container-package,
          .cabs-search-page .cabs-search-results {
            padding-left: 15px !important;
            padding-right: 15px !important;
          }

          .cabs-search-page .cabs-search-hero {
            margin-bottom: 32px !important;
            padding: 16px 0 !important;
          }

          .cabs-search-page .cabs-search-hero .row {
            margin-left: 0 !important;
            margin-right: 0 !important;
          }

          .cabs-search-page .cabs-search-filter-card {
            width: 100% !important;
            border-radius: 12px !important;
            margin: 0 auto !important;
          }

          .cabs-search-page .cabs-search-filter-body {
            padding: 20px !important;
          }

          .cabs-search-page .heading-div {
            gap: 10px;
          }
        }

        @media (max-width: 360px) {
          .cabs-search-page .cabs-search-hero {
            padding: 14px 0 !important;
          }

          .cabs-search-page .cabs-search-filter-body {
            padding: 18px !important;
          }
        }
      `}</style>
    </div>
  );
}

export default CabsListing;
